import { RootState } from '@/app/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  DataByTin,
  FactoringClient,
  GetDidoxDocResponse,
  GetDidoxDocsParams,
  GetDidoxDocuments,
  GetDidoxUsersResponse,
} from './types';
import { DefaultResponse } from '@/app/helpers/types';
import i18n from '@/app/i18n';
import encrypt from '@/shared/factoring-employees/helpers/encrypt';

const lang = i18n.language.split('_')[0] || 'uz';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_DIDOX_API_URL,
  fetchFn: async (...args) => {
    return fetch(...args).then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      return response;
    });
  },
  prepareHeaders: async (headers, { getState, endpoint }) => {
    const token = (getState() as RootState).auth.userKey;

    if (token) {
      headers.set('user-key', token);
    }

    headers.set('accept-language', lang);

    if (endpoint === 'editCompanyEmployee') {
      headers.set(
        'partner-authorization',
        await encrypt(
          JSON.stringify({
            token: import.meta.env.VITE_DIDOX_PARTNER_TOKEN,
            iat: new Date().toUTCString(),
          })
        )
      );
    }

    return headers;
  },
});

export const didoxApi = createApi({
  reducerPath: 'didox',
  baseQuery,
  tagTypes: ['Employees'],
  endpoints: (builder) => ({
    getDoc: builder.query<DefaultResponse<GetDidoxDocResponse>, string>({
      query: (docId) => ({
        url: `/v1/documents/${docId}`,
        method: 'GET',
      }),
    }),
    getDocs: builder.query<GetDidoxDocuments, GetDidoxDocsParams>({
      query: (body) => ({
        url: !import.meta.env.VITE_DIDOX_BASE_URL_DOCS
          ? '/v2/documents/search'
          : import.meta.env.VITE_DIDOX_BASE_URL_DOCS + '/v2/documents/search',
        method: 'POST',
        body: {
          ...body,
          // Из массива строк делаем строку с разделителем "," и удаляем последнюю запятую
          factoringDocIds: body?.factoringDocIds
            ?.join(',')
            .replace(/,(\s+)?$/, ''),
        },
      }),
    }),
    getFactoringClients: builder.query<
      { data: FactoringClient[]; total: number },
      { page: number; size: number; search: string }
    >({
      query: (params) => ({
        url: 'v1/factoring/banks/clients',
        method: 'GET',
        params,
      }),
    }),
    getDocHtml: builder.query<string, string>({
      query: (docId) => ({
        url: `/v1/documents/${docId}/html/${lang}`,
        method: 'GET',
        responseHandler: (response) => response.text(),
      }),
    }),
    getDocPdf: builder.query<string, string>({
      query: (docId) => ({
        url: `${
          import.meta.env.VITE_DIDOX_API_URL_2 || ''
        }/v1/documents/${docId}/pdf`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    getDocBase64: builder.query<DefaultResponse<string>, string>({
      query: (docId) => ({
        url: `/v1/documents/${docId}/documentBase64`,
        method: 'GET',
      }),
    }),
    getStringToSign: builder.query<
      DefaultResponse<Record<string, unknown>>,
      { docId: string; action: string; comment?: string }
    >({
      query: ({ docId, action, comment }) => ({
        url: `/v1/documents/${docId}/tosign`,
        method: 'POST',
        body: { action, comment },
      }),
    }),
    getDataByTin: builder.query<DataByTin, { tin: string }>({
      query: ({ tin }) => ({
        url: `/v1/utils/info/${tin}`,
        method: 'GET',
      }),
    }),
    editCompanyEmployee: builder.mutation<
      DefaultResponse,
      {
        gnkpermissions: string;
        internalpermissions: string;
        is_director: number;
      }
    >({
      query: (body) => ({
        url: `${import.meta.env.VITE_DIDOX_API_URL_3 || ''}/v1/profile/company/users`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Employees'],
    }),
    getLinkedUsers: builder.query<GetDidoxUsersResponse, { lang: string }>({
      query: ({ lang }) => ({
        url: `${import.meta.env.VITE_DIDOX_API_URL_3 || ''}/v1/profile/company/users/${lang}`,
        method: 'GET',
      }),
      providesTags: ['Employees'],
    }),
    getDataByPinfl: builder.query<
      {
        fullName: string;
        fullname: string;
        shortName: string;
        shortname: string;
      },
      { pinfl: string }
    >({
      query: ({ pinfl }) => ({
        url: `${import.meta.env.VITE_DIDOX_API_URL_3 || ''}/v1/utils/info/${pinfl}`,
        method: 'GET',
      }),
    }),
    getFactoringClientsExport: builder.query<Blob, void>({
      query: () => ({
        url: '/v1/factoring/banks/clients/export',
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetDocsQuery,
  useLazyGetDocsQuery,
  useGetFactoringClientsQuery,
  useGetDocQuery,
  useLazyGetDocQuery,
  useGetDocHtmlQuery,
  useLazyGetDocHtmlQuery,
  useLazyGetDocPdfQuery,
  useLazyGetStringToSignQuery,
  useLazyGetDocBase64Query,
  useLazyGetDataByTinQuery,
  useEditCompanyEmployeeMutation,
  useGetLinkedUsersQuery,
  useLazyGetDataByPinflQuery,
  useLazyGetFactoringClientsExportQuery,
} = didoxApi;
